import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { usePaymentLanguage } from '../../lib/paymentLanguage';
import { usePaymentAnalytics } from '../../hooks/usePaymentAnalytics';
import { ANALYTICS_EVENTS, AnalyticsProperties } from '../../lib/analytics';
import { startBankLinkSequence } from '../../lib/bankLinkSequence';

export interface TransferBankRequisites {
    sender_bank_code?: 'tinkoff' | 'vtb' | 'sber';
    deep_links?: Partial<Record<'ios' | 'android' | 'web', Record<string, string[]>>>;
}

function IosSberAction({ links, onClick }: { links: string[]; onClick: (url: string, index: number) => void }) {
    const { t } = usePaymentLanguage();
    const [opening, setOpening] = useState(false);
    const [attempted, setAttempted] = useState(false);
    const cancel = useRef<(() => void) | null>(null);
    const running = useRef(false);
    useEffect(() => () => cancel.current?.(), []);
    const stop = () => {
        cancel.current?.();
        running.current = false;
        setOpening(false);
    };
    const open = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (running.current) return;
        running.current = true;
        setOpening(true);
        setAttempted(true);
        onClick(links[0], 0);
        cancel.current = startBankLinkSequence(links, () => {
            running.current = false;
            setOpening(false);
        });
    };
    return <>
        <a href={links[0]} onClick={open} aria-disabled={opening} aria-busy={opening}
            className="transfer-bank-action transfer-bank-action--sber">
            <span className="transfer-bank-action-logo" aria-hidden="true">
                <img src="/assets/banks/sber.svg" alt="" />
            </span>
            <span>{t(opening ? 'Открываем {bank}…' : 'Оплатить через {bank}', { bank: 'Сбер' })}</span>
            <ArrowUpRight className="transfer-bank-action-arrow" aria-hidden="true" />
        </a>
        {attempted && <details className="transfer-bank-alternatives" onToggle={event => {
            if (event.currentTarget.open) stop();
        }}>
            <summary>{t('Не открывается приложение?')}</summary>
            <p>{t('Попробуйте другой вариант открытия или выполните перевод вручную по указанным реквизитам.')}</p>
            {links.map((url, index) => <a key={url} href={url} onClick={() => {
                stop();
                onClick(url, index);
            }}>{t('Открыть {bank} — вариант {number}', { bank: 'Сбер', number: index + 1 })}</a>)}
        </details>}
    </>;
}

export function TransferBankOptions({ requisites, analyticsProperties = {} }: {
    requisites: TransferBankRequisites;
    analyticsProperties?: AnalyticsProperties;
}) {
    const trackAnalyticsEvent = usePaymentAnalytics();
    const { t } = usePaymentLanguage();
    const bank = requisites.sender_bank_code;
    if (!bank) return null;
    const os = /Android/i.test(navigator.userAgent) ? 'android'
        : /iPhone|iPad|iPod/i.test(navigator.userAgent)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'ios' : 'web';
    const deviceLinks = requisites.deep_links?.[os]?.[bank] || [];
    const links = deviceLinks.length ? deviceLinks : requisites.deep_links?.web?.[bank] || [];
    if (!links.length) return null;
    const name = { tinkoff: 'Т-Банк', vtb: 'ВТБ', sber: 'Сбер' }[bank];
    const logo = { tinkoff: 'tbank.png', vtb: 'vtb.svg', sber: 'sber.svg' }[bank];
    const trackBankClick = (url: string, index: number) => {
        const properties = {
            ...analyticsProperties,
            bank_id: bank,
            platform: os === 'web' ? 'desktop' : os,
            interaction: url.startsWith('https://') ? 'open_web' : 'open_app',
            link_index: index + 1,
        };
        trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_ACTION_CLICKED,
            { ...properties, action: 'open_bank' }, { flushImmediately: true });
        trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_BANK_OPEN_CLICKED,
            properties, { flushImmediately: true });
    };
    return <div className="transfer-bank-actions">
        {bank === 'sber' && os === 'ios' && deviceLinks.length > 1
            ? <IosSberAction key={JSON.stringify(links)} links={links} onClick={trackBankClick} />
            : links.map((url, index) => <a key={url} href={url}
            onClick={() => trackBankClick(url, index)}
            className={`transfer-bank-action transfer-bank-action--${bank}`}>
            <span className="transfer-bank-action-logo" aria-hidden="true">
                <img src={`/assets/banks/${logo}`} alt="" />
            </span>
            <span>{t('Оплатить через {bank}', { bank: name })}{links.length > 1 ? ` (${index + 1})` : ''}</span>
            <ArrowUpRight className="transfer-bank-action-arrow" aria-hidden="true" />
        </a>)}
    </div>;
}
